import React, { useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { useExitAnim } from "../../hooks/useExitAnim";
import { useDismissedConversations } from "../../hooks/useDismissedConversations";
import { ProofGridView } from "./proof/ProofGridView";
import { formatDateShort } from "../../utils/formatters";
import type { ConversationNav } from "../../hooks/data-fetching/useConversationPreviews";

// =============================================================================
// DismissedLeadsGridModal
// =============================================================================
// The "3x3 grid style" view of the dismissed pile (S4-grid-modal). Reuses
// ProofGridView as-is — org-scoped so unassigned rows (NULL ghl_assigned_user_id)
// are reachable, which a setter-scoped fetch can never show (S4b-grid-scope).
//
// Owns its OWN useDismissedConversations instance (CTX-arch, Lens A): the
// panel's list and this grid are independent fetches, each refetching on its
// own triggers. After a bulk dismiss/kill fired from inside the grid, this
// modal refetches its own rows (so the dismissed card vanishes) AND calls the
// tab-supplied onAfterBulkDismiss so the tab can refresh the leaderboard total.
// =============================================================================

interface DismissedLeadsGridModalProps {
  isOpen: boolean;
  onClose: () => void;
  organizationId: string;
  timezone: string;
  dateRange: { start: Date | null; end: Date | null };
  /** True org-wide total from the leaderboard RPC — the header never lies with "1000 of 1000". */
  total: number;
  onConversationClick: (
    conversationId: string,
    contactName: string,
    leadInstagram: string | null,
    highlightTimestamps?: { highlight: string; anchor?: string },
    nav?: ConversationNav
  ) => void;
  onAfterBulkDismiss: (ids: string[], dismissType: "dismissed" | "dead") => void;
}

// Module-level stable reference. An inline `[]` would be a new array every
// render → useDismissedConversations' fetchRows dep churns → effect re-fires →
// setState → infinite render loop ("Maximum update depth exceeded"). orgScope
// ignores the value anyway; it just has to be referentially stable.
const EMPTY_SETTER_IDS: string[] = [];

export const DismissedLeadsGridModal: React.FC<DismissedLeadsGridModalProps> = ({
  isOpen,
  onClose,
  organizationId,
  timezone,
  dateRange,
  total,
  onConversationClick,
  onAfterBulkDismiss,
}) => {
  // Keep the modal mounted through the 200ms exit so anim-modal-out plays.
  const { mounted, closing } = useExitAnim(isOpen, 200);

  // Org-scoped (setterIds ignored under orgScope) — reaches unassigned
  // dismissals the setter-filtered panel list can't. Gate the fetch on `mounted`
  // so a closed modal doesn't pull ~1000 org-wide rows every page load.
  const { dismissed, refresh, isLoading, error } = useDismissedConversations({
    organizationId: mounted ? organizationId : null,
    setterIds: EMPTY_SETTER_IDS,
    dateRange,
    timezone,
    orgScope: true,
  });

  // ESC closes the modal (no nested drill-down state here, unlike SetterProofModal).
  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [isOpen, onClose]);

  // ConversationEntry shape is NOT exported from ProofGridView — inline the literal.
  const mapped = useMemo(
    () =>
      dismissed.map((c) => ({
        conversation_id: c.id,
        contact_name: c.contact_name,
        lead_instagram: null,
        bucket: c.dismiss_type ?? "dismissed",
        time_label: c.suffering_dismissed_at
          ? formatDateShort(new Date(c.suffering_dismissed_at), timezone)
          : "",
      })),
    [dismissed, timezone]
  );

  const rangeLabel =
    dateRange.start && dateRange.end
      ? `${formatDateShort(dateRange.start, timezone)} - ${formatDateShort(dateRange.end, timezone)}`
      : "";

  if (!mounted) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[9998] flex items-start justify-center pt-8 pb-8"
      data-testid="dismissed-grid-modal"
    >
      {/* Backdrop */}
      <div
        className={`${closing ? "anim-backdrop-out" : "anim-backdrop-in"} absolute inset-0 bg-black/50 backdrop-blur-sm`}
        onClick={onClose}
      />

      {/* Modal — anim-modal-in on the NON-glass wrapper (transform on detail-overlay = backdrop-filter repaint blur) */}
      <div
        className={`${closing ? "anim-modal-out" : "anim-modal-in"} w-full max-w-[85vw] mx-4 flex min-h-0`}
      >
        <div className="relative detail-overlay rounded-xl shadow-2xl w-full max-h-[85vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-black/[0.08] shrink-0">
            <div>
              <h2 className="token-18 text-glass">Dismissed Leads</h2>
              <p className="token-14 text-glass-muted">
                {error ? (
                  <span className="text-ds-red">Couldn&apos;t load dismissed leads.</span>
                ) : isLoading && mapped.length === 0 ? (
                  "Loading…"
                ) : (
                  <>
                    <span className="font-mono tabular-nums">{Math.min(mapped.length, 1000)}</span>{" "}
                    of <span className="font-mono tabular-nums">{total}</span> dismissed leads ·{" "}
                    <span className="font-mono tabular-nums">{rangeLabel}</span>
                  </>
                )}
              </p>
            </div>
            <button
              onClick={onClose}
              className="anim-tap p-2 hover:bg-black/[0.05] rounded-lg transition-colors"
              aria-label="Close modal"
            >
              <svg
                className="w-5 h-5 text-glass-muted"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto p-6" style={{ overscrollBehavior: "contain" }}>
            <ProofGridView
              conversations={mapped}
              organizationId={organizationId}
              timezone={timezone}
              viewedSetterId={null}
              onConversationClick={onConversationClick}
              activeDateRange={
                dateRange.start && dateRange.end
                  ? { start: dateRange.start, end: dateRange.end }
                  : null
              }
              onAfterBulkDismiss={(ids, dismissType) => {
                // Refetch this modal's own rows first so the dismissed card
                // vanishes here, THEN tell the tab so it can refresh the
                // leaderboard total (S4-grid-modal wiring, Lens A r5/r6).
                refresh();
                onAfterBulkDismiss(ids, dismissType);
              }}
            />
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};
